'use client';

import { useMemo, useRef, useState, type ReactNode } from 'react';
import { useTranslation } from 'react-i18next';
import {
  buildStops,
  indexToValue,
  parseAmount,
  valueToIndex,
} from '@/lib/range-slider-scale';
import { Input } from '@/components/ui/input';
import { Slider, SliderThumb } from '@/components/ui/slider';

export function RangeSliderFilter({
  label,
  min,
  max,
  onChange,
  maxPos,
  maxAbsNeg = 0,
  employees = false,
  lock,
}: {
  label: ReactNode;
  min?: number;
  max?: number;
  onChange: (min: number | undefined, max: number | undefined) => void;
  maxPos: number;
  maxAbsNeg?: number;
  employees?: boolean;
  lock: 'user' | null;
}) {
  const { t, i18n } = useTranslation();
  const stops = useMemo(
    () => buildStops({ maxAbsNeg, maxPos, first: employees ? 1 : 1000 }),
    [maxAbsNeg, maxPos, employees],
  );
  const values = [
    valueToIndex(min, stops, 'min'),
    valueToIndex(max, stops, 'max'),
  ];
  // A thumb can share a stop with an exact off-ladder bound. Track which
  // thumb is being edited so the untouched bound never gets snapped away.
  const movingThumb = useRef<'min' | 'max' | null>(null);
  const format = (n: number) =>
    employees
      ? new Intl.NumberFormat(i18n.language, {
          maximumFractionDigits: 0,
        }).format(n)
      : new Intl.NumberFormat(i18n.language, {
          style: 'currency',
          currency: 'EUR',
          maximumFractionDigits: Math.abs(n) >= 1e6 ? 1 : 0,
          notation: Math.abs(n) >= 1e6 ? 'compact' : 'standard',
        }).format(n);
  const selection =
    min !== undefined && max !== undefined
      ? `${format(min)} – ${format(max)}`
      : min !== undefined
        ? `${t('search.chips.from')} ${format(min)}`
        : max !== undefined
          ? `${t('search.chips.upTo')} ${format(max)}`
          : t('search.range.any');
  const commit = (side: 'min' | 'max', value: number | undefined) => {
    let nextMin = side === 'min' ? value : min;
    let nextMax = side === 'max' ? value : max;
    if (nextMin !== undefined && nextMax !== undefined && nextMin > nextMax)
      [nextMin, nextMax] = [nextMax, nextMin];
    onChange(nextMin, nextMax);
  };
  return (
    <div className="w-full min-w-0 space-y-2">
      {label}
      <div className="text-xs text-muted-foreground" aria-live="polite">
        {selection}
      </div>
      <div className="px-5 lg:px-2">
        <Slider
          value={values}
          min={0}
          max={stops.length - 1}
          step={1}
          disabled={!!lock}
          className="h-11 lg:h-6"
          onPointerDownCapture={() => {
            movingThumb.current = null;
          }}
          onValueChange={(next) => {
            const side =
              movingThumb.current ?? (next[0] !== values[0] ? 'min' : 'max');
            let nextMin =
              side === 'min' ? indexToValue(next[0], stops, 'min') : min;
            let nextMax =
              side === 'max' ? indexToValue(next[1], stops, 'max') : max;
            // Exact off-stop bounds can overlap visually. Moving one thumb
            // constrains it to the untouched exact bound, never changes both.
            if (
              nextMin !== undefined &&
              nextMax !== undefined &&
              nextMin > nextMax
            ) {
              if (side === 'min') nextMin = nextMax;
              else nextMax = nextMin;
            }
            onChange(nextMin, nextMax);
          }}
        >
          <SliderThumb
            aria-label={t('search.range.minimum')}
            className="size-10 lg:size-4"
            onFocus={() => {
              movingThumb.current = 'min';
            }}
            onPointerDown={() => {
              movingThumb.current = 'min';
            }}
          />
          <SliderThumb
            aria-label={t('search.range.maximum')}
            className="size-10 lg:size-4"
            onFocus={() => {
              movingThumb.current = 'max';
            }}
            onPointerDown={() => {
              movingThumb.current = 'max';
            }}
          />
        </Slider>
      </div>
      <div className="flex justify-between gap-2 text-[0.625rem] text-muted-foreground">
        <span>{format(stops[0])}</span>
        <span>{format(stops[stops.length - 1])}</span>
      </div>
      <div className="grid grid-cols-2 gap-2">
        {(['min', 'max'] as const).map((side) => (
          <AmountInput
            key={`${side}:${side === 'min' ? min : max}:${i18n.language}`}
            disabled={!!lock}
            label={t(
              side === 'min' ? 'search.range.minimum' : 'search.range.maximum',
            )}
            value={side === 'min' ? min : max}
            onCommit={(value) => commit(side, value)}
          />
        ))}
      </div>
    </div>
  );
}

/** Remounted for a changed bound; draft text stays local until blur/Enter. */
function AmountInput({
  value,
  label,
  disabled,
  onCommit,
}: {
  value?: number;
  label: string;
  disabled: boolean;
  onCommit: (value: number | undefined) => void;
}) {
  const { i18n } = useTranslation();
  const formatValue = (amount: number | undefined) => amount === undefined ? ''
    : new Intl.NumberFormat(i18n.language, { maximumFractionDigits: 0 }).format(amount);
  const [text, setText] = useState(() => formatValue(value));
  const commit = () => {
    const parsed = parseAmount(text);
    if (text.trim() && parsed === undefined) {
      setText(formatValue(value));
      return;
    }
    setText(formatValue(parsed));
    onCommit(parsed);
  };
  return (
    <Input
      type="text"
      disabled={disabled}
      className="h-11 min-w-0 text-base lg:h-8 lg:text-xs"
      aria-label={label}
      placeholder={label}
      value={text}
      onChange={(e) => setText(e.target.value)}
      onBlur={commit}
      onKeyDown={(e) => {
        if (e.key === 'Enter') {
          e.preventDefault();
          commit();
        }
      }}
    />
  );
}
